import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { AbsenteeismStatusOptions, CustomSelect, FilterOptions, TermFilter } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/student-absenteeism-report/StudentAbsenteeismReportAction";
import { useStudentAbsenteeismReportUIContext } from "../StudentAbsenteeismReportUIContext";

const prepareFilter = (queryParams, values) => {
  let { term, date, dateEnd, sectionId, studentId, studentNumber, parentName, absenteeismStatus, explanation } = values;
  studentNumber = studentNumber === "" ? null : studentNumber;
  const newQueryParams = { ...queryParams };
  const filter = { date, dateEnd, sectionId, studentId, studentNumber, parentName, absenteeismStatus, explanation };
  newQueryParams.filter = filter;
  newQueryParams.term = term;
  return newQueryParams;
};

export function StudentAbsenteeismReportFilter() {
  // Student Absenteeism Report UI Context
  const studentAbsenteeismReportUIContext = useStudentAbsenteeismReportUIContext();
  const studentAbsenteeismReportUIProps = useMemo(() => {
    return {
      setQueryParams: studentAbsenteeismReportUIContext.setQueryParams,
      queryParams: studentAbsenteeismReportUIContext.queryParams,
    };
  }, [studentAbsenteeismReportUIContext]);

  const dispatch = useDispatch();
  const { currentActiveTerm, sections, students } = useSelector(
    (state) => ({
      currentActiveTerm: state.auth.user.currentActiveTerm,
      sections: state.studentAbsenteeismReport.sections,
      students: state.studentAbsenteeismReport.students
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(actions.getSections());
  }, [dispatch]);

  const applyFilter = (values) => {
    const newQueryParams = prepareFilter(studentAbsenteeismReportUIProps.queryParams, values);
    if (!isEqual(newQueryParams, studentAbsenteeismReportUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      studentAbsenteeismReportUIProps.setQueryParams(newQueryParams);
    }
  };

  const sectionOptions = FilterOptions.concat(sections);
  const studentOptions = FilterOptions.concat(students);
  const absenteeismStatusOptions = FilterOptions.concat(AbsenteeismStatusOptions.slice(1));

  return (
    <Formik
      initialValues={{
        term: currentActiveTerm,
        studentNumber: "",
        parentName: "",
        explanation: ""
      }}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({
        values,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit} className="form form-label-right">
          <TermFilter
            handleSubmit={handleSubmit}
            handleBlur={handleBlur}
            handleChange={handleChange}
            onChange={e => {
              if (values.sectionId) dispatch(actions.getStudents(e.target.value, values.sectionId));
              setFieldValue("studentId", undefined);
            }}
            values={values}
          />
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Tarih
            </label>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="date"
                placeholder="Başlangıç"
                onBlur={handleBlur}
                value={values.date}
                onChange={(e) => {
                  setFieldValue("date", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
            <div className="col-lg-2">
              <input
                type="date"
                className="form-control"
                name="dateEnd"
                placeholder="Bitiş"
                onBlur={handleBlur}
                value={values.dateEnd}
                onChange={(e) => {
                  setFieldValue("dateEnd", e.target.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Şube
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={sectionOptions}
                value={values.sectionId}
                placeholder="Şube"
                onBlur={handleBlur}
                onChange={e => {
                  if (e.value) dispatch(actions.getStudents(values.term, e.value));
                  dispatch(actions.cleanStudentsState());
                  setFieldValue("sectionId", e.value);
                  setFieldValue("studentId", undefined);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-sm-2"></div>
            <label className="form-label col-form-label col-sm-2">
              Öğrenci
            </label>
            <div className="col-lg-4">
              <CustomSelect 
                options={studentOptions}
                value={values.studentId}
                placeholder="Öğrenci"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("studentId", e.value);
                  handleSubmit();
                }}
              />
            </div>
          </div>
          <div className="form-group row">
            <div className="col-lg-2">
              <input
                type="number"
                className="form-control"
                name="studentNumber"
                min={1}
                placeholder="Öğrenci Numarası"
                onBlur={handleBlur}
                value={values.studentNumber}
                onChange={(e) => {
                  setFieldValue("studentNumber", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Öğrenci Numarası</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="parentName"
                placeholder="Veli Adı Soyadı"
                onBlur={handleBlur}
                value={values.parentName}
                onChange={(e) => {
                  setFieldValue("parentName", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Veli Adı Soyadı</b>
              </small>
            </div>
            <div className="col-lg-2">
              <CustomSelect 
                options={absenteeismStatusOptions}
                value={values.absenteeismStatus}
                placeholder="Durum"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue("absenteeismStatus", e.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Durum</b>
              </small>
            </div>
            <div className="col-lg-2">
              <input
                type="text"
                className="form-control"
                name="explanation"
                placeholder="Açıklama"
                onBlur={handleBlur}
                value={values.explanation}
                onChange={(e) => {
                  setFieldValue("explanation", e.target.value);
                  handleSubmit();
                }}
              />
              <small className="form-text text-muted">
                <b>Açıklama</b>
              </small>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
}